import { useEffect, useRef, useState } from 'react';

import Compressor from 'compressorjs';
import ReactQuill, { type DeltaStatic, type EmitterSource } from 'react-quill-new';

// Loaded here (not entry.css) because Tailwind v4's @import inlines CSS, pushing any later @import past the spec's "@imports first" rule.
import 'react-quill-new/dist/quill.snow.css';

import { useToast } from '@/components/ui/toaster';

class ImageInsertionHandler {
  private quillRef: React.RefObject<ReactQuill | null>;
  private toast: ReturnType<typeof useToast>;
  /**
   * Defaults
   * Set max to 2MB to match DP richtext editor
   * Set max width and height to 1000px to match DP richtext editor
   * Hardcoded values here as this handler is very specific to this richtext editor
   */
  private imageMaxSizeKB = 2000000;
  private imageMaxWidth = 1000;
  private imageMaxHeight = 1000;
  private imageQuality = 0.8;

  constructor(
    quillRef: React.RefObject<ReactQuill | null>,
    toast: ReturnType<typeof useToast>,
    imageMaxSizeKB?: number,
    imageMaxWidth?: number,
    imageMaxHeight?: number,
    imageQuality?: number,
  ) {
    this.quillRef = quillRef;
    this.toast = toast;
    this.imageMaxSizeKB = imageMaxSizeKB ?? this.imageMaxSizeKB;
    this.imageMaxWidth = imageMaxWidth ?? this.imageMaxWidth;
    this.imageMaxHeight = imageMaxHeight ?? this.imageMaxHeight;
    this.imageQuality = imageQuality ?? this.imageQuality;
  }

  async handleImageInsert(delta: DeltaStatic, source: EmitterSource): Promise<string | null> {
    if (source === 'user' && delta.ops.length > 0 && delta.ops.some(this.isInsertImageOp)) {
      const imageOps = delta.ops.filter(this.isInsertImageOp);

      for (let i = 0; i < imageOps.length; i++) {
        const op = imageOps[i];
        const imageIndex = delta.ops.findIndex((deltaOp) => deltaOp === op);
        try {
          await this.processImage(delta, op as { insert: { image: string } }, imageIndex);
        } catch (error) {
          console.error('Failed to process pasted image:', error);
          this.toast.error('That image could not be added. Please try a different one.');
        }
      }

      // Return the final content after all images are processed
      const editor = this.quillRef.current?.getEditor();
      if (editor) {
        return editor.root.innerHTML;
      }
    }
    return null;
  }

  public isInsertImageOp(op: DeltaStatic['ops'][number]): boolean {
    return Boolean(typeof op.insert === 'object' && op.insert?.image);
  }

  private async processImage(delta: DeltaStatic, imageOp: { insert: { image: string } }, imageIndex: number) {
    let finalImageBase64 = '';
    const imageBase64 = imageOp.insert.image;

    const imageRetainValue = this.getInsertImageRetainValue(delta, imageIndex);
    this.removeImage(imageRetainValue); // Remove early to avoid flashing large images while processing runs async

    const imageFile = await this.getImageFile(imageBase64);
    if (imageFile.size > this.imageMaxSizeKB) {
      const compressImage = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
          new Compressor(file, {
            quality: this.imageQuality,
            maxWidth: this.imageMaxWidth,
            maxHeight: this.imageMaxHeight,
            success: (compressedFile) => resolve(compressedFile as File),
            error: reject,
          });
        });
      };
      const resizedImageFile = await compressImage(imageFile);
      if (resizedImageFile.size > this.imageMaxSizeKB) {
        this.toast.error(`Image exceeds max size of ${this.imageMaxSizeKB / 1000 / 1000}MB`);
        return;
      }
      finalImageBase64 = await this.getImageBase64(resizedImageFile);
    } else {
      finalImageBase64 = imageBase64;
    }

    this.reInsertImage(imageRetainValue, finalImageBase64);

    // Update content and trigger onChange after image processing is complete to avoid sub-loop onChange events firing
    const editor = this.quillRef.current?.getEditor();
    if (editor) {
      const finalContent = editor.root.innerHTML;
      return finalContent;
    }
  }

  private async getImageFile(url: string): Promise<File> {
    if (!url.startsWith('data:')) {
      throw new Error('Unsupported image format. Only base64 data URLs are supported.');
    }

    const [meta, base64] = url.split(',');
    const mime = meta.match(/data:(.*);base64/)?.[1] || 'image/png';
    const binary = window.atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new File([bytes], 'image.png', { type: mime });
  }

  private async getImageBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  private getInsertImageRetainValue(delta: DeltaStatic, imageIndex: number): number {
    const retainValue = delta.ops.slice(0, imageIndex).reduce((sum, op) => sum + (typeof op.retain === 'number' ? op.retain : 0), 0);
    return retainValue;
  }

  private removeImage(imageRetainValue: number) {
    const editor = this.quillRef.current?.getEditor();
    editor?.updateContents([{ retain: imageRetainValue }, { delete: 1 }], 'silent');
  }

  private reInsertImage(imageRetainValue: number, finalImageUrl: string) {
    const editor = this.quillRef.current?.getEditor();
    editor?.updateContents([{ retain: imageRetainValue }, { insert: { image: finalImageUrl } }], 'silent');
  }
}

interface RichTextEditorProps extends Omit<ReactQuill.ReactQuillProps, 'value' | 'onChange'> {
  value?: string | null;
  onChange?: (content: string) => void;
  disabled?: boolean;
}

const defaultModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }, 'bold', 'italic', 'underline', 'strike', { color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }, 'image', 'link'],
  ],
  clipboard: {
    matchVisual: false,
  },
} as const;

export const RichText = ({ value = '', onChange = () => {}, modules = defaultModules, disabled = false, ...rest }: RichTextEditorProps) => {
  const [content, setContent] = useState(value);
  const quillRef = useRef<ReactQuill>(null);
  const toast = useToast();

  // Create image insertion handler instance
  const imageHandler = new ImageInsertionHandler(quillRef, toast);

  useEffect(() => {
    setContent(value);
  }, [value]);

  const handleChange = async (newContent: string, delta: DeltaStatic, source: EmitterSource) => {
    if (disabled) return;
    if (source === 'user') {
      const hasImages = delta.ops.length > 0 && delta.ops.some((op) => imageHandler.isInsertImageOp(op));

      if (hasImages) {
        const finalContent = await imageHandler.handleImageInsert(delta, source);
        if (finalContent) {
          setContent(finalContent);
          onChange(finalContent);
        }
      } else {
        setContent(newContent);
        onChange(newContent);
      }
    }
  };

  return (
    <div
      className={`[&_.ql-container]:!border-grey-3 [&_.ql-container]:rounded-b-lg [&_.ql-container]:border [&_.ql-container]:${disabled ? 'bg-grey-1' : 'bg-white'} [&_.ql-toolbar]:!border-grey-3 [&_.ql-editor]:min-h-[150px] [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border [&_.ql-toolbar]:${disabled ? 'bg-grey-1' : 'bg-white'} ${disabled ? '[&_.ql-container]:cursor-not-allowed [&_.ql-container]:bg-gray-50 [&_.ql-editor]:cursor-not-allowed [&_.ql-toolbar]:cursor-not-allowed [&_.ql-toolbar]:bg-gray-50' : ''}`}>
      <ReactQuill
        ref={quillRef}
        value={content ?? undefined}
        onChange={handleChange}
        modules={modules}
        {...rest}
        readOnly={disabled}
        className={disabled ? 'bg-grey-1 pointer-events-none' : ''}
      />
    </div>
  );
};
